/**
 * Dashboard activity summary cards.
 */
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { useAsterController } from "context";
import { VText } from "form";

/**
 * Shows high-level learning stats for the current learner.
 */
const DashboardSummary = () => {
  const { t } = useTranslation("common");

  const [controller] = useAsterController();
  const { courses, meetEvents, commentCount, loadedDashboard } = controller;

  const [values, setValues] = useState({});

  useEffect(() => {
    if ((courses.data || []).length) {
      const enrolls = (courses.data || []).filter((v) => v.enrollment_status);
      if (!enrolls.length) return;

       const enrollQuiz = enrolls.reduce((a, b) => a + (b.moduleMapper?.length || 0), 0);
      const totalQuiz = Math.round((courses.total * enrollQuiz) / enrolls.length);

      setValues({
        course_enroll: enrolls.length,
        course_total: courses.total,
        course_percent: (enrolls.length * 100) / courses.total,
        quiz_enroll: enrollQuiz,
        quiz_total: totalQuiz,
        quiz_percent: (enrollQuiz * 100) / totalQuiz,
      });
    }
  }, [courses.data, courses.total]);

  return (
    <>
      <Box className="w-full flex justify-center lg:justify-start pt-8">
        <VText className="text-2xl">{t("Summary")}</VText>
      </Box>
      <Box className="w-full my-5 rounded-md border border-solid border-gray-200 dark:border-gray-500 px-8 py-6">
        <Box className="flex flex-col">
          <Box className="w-full mb-5">
            {loadedDashboard ? (
              <>
                <Box className="flex justify-between">
                  <VText>{t("Course")}</VText>
                  <VText>
                    {values.course_enroll || 0} / {values.course_total || 0}
                  </VText>
                </Box>
                <Box className="w-full bg-gray-100 h-1.5 dark:bg-gray-700">
                  <Box className="bg-gray-400 h-1.5" sx={{ width: `${values.course_percent || 0}%` }} />
                </Box>
              </>
            ) : (
              <Skeleton className="w-full h-6" />
            )}
          </Box>
          <Box className="w-full mb-5">
            {loadedDashboard ? (
              <>
                <Box className="flex justify-between">
                  <VText>{t("Quiz")}</VText>
                  <VText>
                    {values.quiz_enroll || 0} / {values.quiz_total || 0}
                  </VText>
                </Box>
                <Box className="w-full bg-gray-100 h-1.5 dark:bg-gray-700">
                  <Box className="bg-gray-400 h-1.5" sx={{ width: `${values.quiz_percent || 0}%` }} />
                </Box>
              </>
            ) : (
              <Skeleton className="w-full h-6" />
            )}
          </Box>
          <Box className="w-full mb-5">
            {loadedDashboard ? (
              <>
                <Box className="flex justify-between">
                  <VText>{t("Virtual class")}</VText>
                  <VText>{meetEvents?.total || 0}</VText>
                </Box>
                <Box className="w-full bg-gray-100 h-1.5 dark:bg-gray-700">
                  <Box className="bg-gray-400 h-1.5" sx={{ width: `${meetEvents?.total || 0}%` }} />
                </Box>
              </>
            ) : (
              <Skeleton className="w-full h-6" />
            )}
          </Box>
          <Box className="w-full mb-5">
            {loadedDashboard ? (
              <>
                <Box className="flex justify-between">
                  <VText>{t("Comments")}</VText>
                  <VText>{commentCount || 0}</VText>
                </Box>
                <Box className="w-full bg-gray-100 h-1.5 dark:bg-gray-700">
                  <Box className="bg-gray-400 h-1.5" sx={{ width: `0%` }} />
                </Box>
              </>
            ) : (
              <Skeleton className="w-full h-6" />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DashboardSummary;
